// src/web/routes/s/$token.tsx - Public share page (matches AssetDetail layout)
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
	AlertCircleIcon,
	ClockIcon,
	DownloadIcon,
	LoaderIcon,
	LockIcon,
	ShieldCheckIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { QueryBoundary } from '@/components/ErrorBoundary'
import { AssetPreview } from '@/components/features/AssetPreview'
import { FileInfo } from '@/components/features/FileInfo'
import { MetadataPanel } from '@/components/features/MetadataPanel'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useDownload } from '@/lib/hooks/useDownload'
import { useMediaWithFallback } from '@/lib/hooks/useSecureMedia'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/s/$token')({
	component: PublicSharePage,
})

interface ShareData {
	expiresAt: string
	hasPassword: boolean
	shareDownloadCount: number
	previewToken: string | null
	asset: {
		id: string
		lifecycleState: string
		dateStaged: string | null
		dateLive: string | null
		downloadCount: number
		metadata: Record<string, string[]>
		thumbnailUrl: string | null
	}
	file: {
		id: string
		filename: string
		size: number
		fileType: string | null
		mediaTypeType: string | null
		mediaTypeSubtype: string | null
		width: number | null
		height: number | null
		duration: number | null
		dateAdded: string | null
	}
}

function formatExpirationDate(isoString: string): string {
	const date = new Date(isoString)
	return date.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

// Storage key for persisted unlock state
const UNLOCK_STORAGE_PREFIX = 'dam_share_unlock_'

interface StoredUnlock {
	previewToken: string
	expiresAt: string
}

function getStoredUnlock(token: string): StoredUnlock | null {
	try {
		const stored = localStorage.getItem(`${UNLOCK_STORAGE_PREFIX}${token}`)
		if (!stored) return null

		const data: StoredUnlock = JSON.parse(stored)

		// Check if expired
		if (new Date(data.expiresAt) < new Date()) {
			localStorage.removeItem(`${UNLOCK_STORAGE_PREFIX}${token}`)
			return null
		}

		return data
	} catch {
		return null
	}
}

function saveUnlock(token: string, previewToken: string, expiresAt: string): void {
	try {
		localStorage.setItem(
			`${UNLOCK_STORAGE_PREFIX}${token}`,
			JSON.stringify({ previewToken, expiresAt })
		)
	} catch {
		// Ignore storage errors
	}
}

function clearUnlock(token: string): void {
	try {
		localStorage.removeItem(`${UNLOCK_STORAGE_PREFIX}${token}`)
	} catch {
		// Ignore storage errors
	}
}

function PublicSharePage() {
	const { token } = Route.useParams()
	const [password, setPassword] = useState('')
	const [passwordError, setPasswordError] = useState<string | null>(null)

	// Check for persisted unlock state on mount
	const storedUnlock = getStoredUnlock(token)
	const [isUnlocked, setIsUnlocked] = useState(!!storedUnlock)
	const [unlockedPreviewToken, setUnlockedPreviewToken] = useState<string | null>(
		storedUnlock?.previewToken ?? null
	)

	// Fetch share info
	const {
		data: shareData,
		isLoading,
		error,
	} = useQuery<ShareData>({
		queryKey: ['share', token],
		queryFn: async () => {
			const response = await fetch(`/api/public/share/${token}/info`)
			if (!response.ok) {
				const data = await response.json()
				throw new Error(data.error || 'Failed to load share')
			}
			return response.json()
		},
	})

	// Determine file type category
	const mediaType = shareData?.file.mediaTypeType?.toLowerCase()
	const mediaSubtype = shareData?.file.mediaTypeSubtype?.toLowerCase()
	const isVideo = mediaType === 'video'
	const isAudio = mediaType === 'audio'
	const isImage = mediaType === 'image'
	const isDocument =
		mediaType === 'text' ||
		(mediaType === 'application' &&
			(mediaSubtype === 'pdf' ||
				mediaSubtype?.includes('msword') ||
				mediaSubtype?.includes('wordprocessing') ||
				mediaSubtype?.includes('ms-excel') ||
				mediaSubtype?.includes('spreadsheet') ||
				mediaSubtype?.includes('ms-powerpoint') ||
				mediaSubtype?.includes('presentation') ||
				mediaSubtype === 'rtf' ||
				mediaSubtype === 'csv'))
	const isPreviewable = isVideo || isAudio || isImage || isDocument

	// Build the API URL (but don't expose it in the DOM)
	// Use unlocked preview token if available (for password-protected shares)
	const effectivePreviewToken = unlockedPreviewToken || shareData?.previewToken
	const previewApiUrl = effectivePreviewToken
		? `/api/public/share/${token}/preview?pt=${effectivePreviewToken}`
		: null

	// Fetch media using S3 streaming (documents use their own blob loading inside DocumentPreview)
	const needsMediaBlob = (isVideo || isAudio || isImage) && !!effectivePreviewToken
	const {
		blobUrl: mediaBlobUrl,
		isLoading: mediaLoading,
		progress: mediaProgress,
		error: mediaError,
	} = useMediaWithFallback(previewApiUrl, needsMediaBlob, shareData?.file.size)

	// Track if current unlock came from localStorage (vs fresh verification)
	const [isStoredUnlock, setIsStoredUnlock] = useState(!!storedUnlock)

	// Handle invalid preview token (e.g., server restarted) by clearing stored unlock
	// Only trigger this for stored tokens, not fresh verifications
	useEffect(() => {
		if (
			isStoredUnlock &&
			mediaError &&
			(mediaError.message?.includes('Invalid') || mediaError.message?.includes('expired'))
		) {
			// Clear stored unlock so user can re-authenticate
			clearUnlock(token)
			setIsUnlocked(false)
			setUnlockedPreviewToken(null)
			setIsStoredUnlock(false)
		}
	}, [mediaError, token, isStoredUnlock])

	// Use the shared download hook with public mode
	const { downloadFile, isDownloading } = useDownload({ mode: 'public', token })

	// Verify password mutation (just unlocks, doesn't download)
	const verifyMutation = useMutation<{ success: boolean; previewToken?: string }, Error, string>({
		mutationFn: async (pwd: string) => {
			const response = await fetch(`/api/public/share/${token}/verify`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ password: pwd }),
			})

			if (!response.ok) {
				const data = await response.json()
				throw new Error(data.error || 'Failed to verify password')
			}

			return response.json()
		},
		onSuccess: (data) => {
			setPasswordError(null)
			setIsUnlocked(true)
			setIsStoredUnlock(false) // Mark as fresh verification, not stored
			if (data.previewToken) {
				setUnlockedPreviewToken(data.previewToken)
				// Persist unlock state until share expires
				if (shareData?.expiresAt) {
					saveUnlock(token, data.previewToken, shareData.expiresAt)
				}
			}
		},
		onError: (err) => {
			setPasswordError(err.message)
		},
	})

	const handleDownload = async () => {
		if (!shareData?.file.id) return

		setPasswordError(null)
		try {
			await downloadFile(shareData.file.id, shareData.file.filename, {
				password: password || undefined,
				previewToken: effectivePreviewToken,
			})

			// On success, mark as unlocked if password-protected
			if (shareData.hasPassword) {
				setIsUnlocked(true)
			}
		} catch (err) {
			// Handle password-related errors
			if (err instanceof Error) {
				if (err.message === 'Password required' || err.message === 'Invalid password') {
					setPasswordError(err.message)
				}
			}
		}
	}

	const handleUnlock = () => {
		setPasswordError(null)
		verifyMutation.mutate(password)
	}

	// Error state
	if (error) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background p-4">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<div className="mx-auto mb-4 w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
							<AlertCircleIcon className="h-8 w-8 text-destructive" />
						</div>
						<CardTitle>Link Unavailable</CardTitle>
						<CardDescription>{error.message}</CardDescription>
					</CardHeader>
				</Card>
			</div>
		)
	}

	// Loading state - matches AssetDetail skeleton
	if (isLoading) {
		return (
			<div className="min-h-dvh flex flex-col px-8 sm:px-14 py-3">
				<div className="flex items-center gap-3 px-4 py-2">
					<Skeleton className="h-8 w-8" />
					<Skeleton className="h-5 w-48" />
					<Skeleton className="h-5 w-14" />
				</div>
				<div className="flex-1 flex gap-3 overflow-hidden">
					<div className="flex-1 p-3">
						<Skeleton className="w-full h-full rounded-xl" />
					</div>
					<div className="w-[380px] hidden lg:block py-3">
						<Skeleton className="w-full h-full rounded-xl" />
					</div>
				</div>
			</div>
		)
	}

	if (!shareData) return null

	const { asset, file } = shareData
	const isExpired = new Date(shareData.expiresAt) < new Date()
	const needsPassword = shareData.hasPassword && !isUnlocked

	// Password wall
	if (needsPassword) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background p-4">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
							<LockIcon className="h-8 w-8 text-primary" />
						</div>
						<CardTitle>Password Protected</CardTitle>
						<CardDescription>Enter the password to view "{file.filename}"</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<Input
							type="password"
							placeholder="Enter password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
						/>
						{passwordError && (
							<p className="text-sm text-destructive text-center">{passwordError}</p>
						)}
						<Button
							onClick={handleUnlock}
							disabled={!password || verifyMutation.isPending}
							className="w-full gap-2"
						>
							{verifyMutation.isPending ? (
								<>
									<Skeleton className="h-4 w-4 rounded-full animate-spin" />
									Verifying...
								</>
							) : (
								<>
									<ShieldCheckIcon className="h-4 w-4" />
									Unlock
								</>
							)}
						</Button>
						<p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
							<ClockIcon className="h-3 w-3" />
							Expires {formatExpirationDate(shareData.expiresAt)}
						</p>
					</CardContent>
				</Card>
			</div>
		)
	}

	// Expired state
	if (isExpired) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background p-4">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<div className="mx-auto mb-4 w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
							<ClockIcon className="h-8 w-8 text-destructive" />
						</div>
						<CardTitle>Link Expired</CardTitle>
						<CardDescription>
							This share link expired on {formatExpirationDate(shareData.expiresAt)}
						</CardDescription>
					</CardHeader>
				</Card>
			</div>
		)
	}

	// For images, use blob URL as thumbnail
	const secureThumbnailUrl = isImage ? mediaBlobUrl : null

	// Documents use sharePreviewUrl prop (passed through AssetPreview → DocumentPreview)
	const fileForPreview = file

	// Tab trigger styles — matches AssetDetail exactly
	const tabTriggerClass = 'rounded-lg text-[13px]'

	// Full asset detail view — matches AssetDetail component layout
	return (
		<div className="h-screen flex flex-col px-8 sm:px-14 py-3">
			{/* Content: [left column] | [right panel] — same flex layout as AssetDetail */}
			<div className="flex-1 flex gap-3 overflow-hidden min-h-0">
				{/* Left column: title bar + preview */}
				<div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
					{/* Title bar — matches AssetDetail style */}
					<div className="flex items-center gap-3 px-4 py-2 flex-shrink-0">
						{/* Logo instead of back button */}
						<div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center overflow-hidden shrink-0">
							<img src="/favicon.svg" alt="DAM" className="w-5 h-5" />
						</div>

						<h1 className="text-sm font-semibold truncate min-w-0">{file.filename}</h1>

						<Badge variant="outline" className="flex-shrink-0 text-xs gap-1">
							<ClockIcon className="h-3 w-3" />
							Expires {formatExpirationDate(shareData.expiresAt)}
						</Badge>
						{shareData.shareDownloadCount > 0 && (
							<Badge variant="secondary" className="flex-shrink-0 text-xs">
								{shareData.shareDownloadCount} downloads
							</Badge>
						)}

						{/* Download button — inline like AssetDetail header actions */}
						<div className="ml-auto flex-shrink-0">
							<Button
								size="sm"
								className="gap-1.5 h-9 rounded-lg"
								onClick={handleDownload}
								disabled={isDownloading}
							>
								{isDownloading ? (
									<LoaderIcon className="h-4 w-4 animate-spin" />
								) : (
									<DownloadIcon className="h-4 w-4" />
								)}
								<span className="hidden sm:inline">
									{isDownloading ? 'Downloading...' : 'Download'}
								</span>
							</Button>
						</div>
					</div>

					{/* Preview area — same padding/structure as AssetDetail */}
					<div className="flex-1 relative flex flex-col min-h-0 min-w-0 p-3">
						{/* Media loading state with progress */}
						{needsMediaBlob && mediaLoading ? (
							<div className="flex-1 relative rounded-xl overflow-hidden">
								{shareData?.asset.thumbnailUrl ? (
									<img
										src={shareData.asset.thumbnailUrl}
										alt="Preview thumbnail"
										className="absolute inset-0 w-full h-full object-contain bg-muted/30"
									/>
								) : (
									<Skeleton className="absolute inset-0 w-full h-full" />
								)}
								<div
									className={cn(
										'absolute inset-0 flex flex-col items-center justify-center gap-3',
										shareData?.asset.thumbnailUrl && 'bg-black/40 backdrop-blur-[2px]'
									)}
								>
									<div className="relative w-16 h-16">
										<svg
											className="w-16 h-16 -rotate-90"
											viewBox="0 0 64 64"
											aria-label={`Loading ${mediaProgress}%`}
										>
											<title>Loading {mediaProgress}%</title>
											<circle
												cx="32"
												cy="32"
												r="28"
												fill="none"
												stroke="currentColor"
												strokeWidth="4"
												className={cn(
													shareData?.asset.thumbnailUrl
														? 'text-white/30'
														: 'text-muted-foreground/20'
												)}
											/>
											<circle
												cx="32"
												cy="32"
												r="28"
												fill="none"
												stroke="currentColor"
												strokeWidth="4"
												strokeLinecap="round"
												className={cn(
													'transition-all duration-300',
													shareData?.asset.thumbnailUrl ? 'text-white' : 'text-muted-foreground'
												)}
												strokeDasharray={`${2 * Math.PI * 28}`}
												strokeDashoffset={`${2 * Math.PI * 28 * (1 - mediaProgress / 100)}`}
											/>
										</svg>
										<span
											className={cn(
												'absolute inset-0 flex items-center justify-center text-sm font-medium',
												shareData?.asset.thumbnailUrl ? 'text-white' : 'text-muted-foreground'
											)}
										>
											{mediaProgress}%
										</span>
									</div>
									<p
										className={cn(
											'text-sm',
											shareData?.asset.thumbnailUrl ? 'text-white/90' : 'text-muted-foreground'
										)}
									>
										Loading preview...
									</p>
								</div>
							</div>
						) : needsMediaBlob && mediaError ? (
							<div className="flex-1 flex items-center justify-center bg-muted/30 rounded-xl">
								<div className="text-center">
									<AlertCircleIcon className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
									<p className="text-sm text-muted-foreground">Failed to load preview</p>
									<p className="text-xs text-muted-foreground mt-1">Download the file to view</p>
								</div>
							</div>
						) : isPreviewable ? (
							<QueryBoundary>
								<AssetPreview
									file={fileForPreview}
									thumbnailUrl={secureThumbnailUrl}
									className="flex-1 min-h-0"
									disableContextMenu
									onOpenDownload={handleDownload}
									sharePreviewUrl={previewApiUrl}
									isPublic
								/>
							</QueryBoundary>
						) : (
							<div className="flex-1 flex items-center justify-center bg-muted/30 rounded-xl">
								<div className="text-center max-w-sm">
									<div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
										<DownloadIcon className="h-8 w-8 text-muted-foreground" />
									</div>
									<p className="text-lg font-medium text-foreground mb-2">Preview not available</p>
									<p className="text-sm text-muted-foreground mb-4">
										This file type cannot be previewed in the browser. Download the file to view its
										contents.
									</p>
									<Button onClick={handleDownload} disabled={isDownloading}>
										{isDownloading ? 'Downloading...' : 'Download File'}
									</Button>
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Right panel — glass card with tabs, matches AssetDetail exactly */}
				<div className="flex-shrink-0 h-full hidden lg:block py-3">
					<Tabs
						defaultValue="info"
						className="w-[380px] h-full rounded-xl border border-border/30 dark:border-white/[0.045] bg-background dark:bg-background/70 backdrop-blur-lg overflow-hidden flex flex-col"
					>
						<div className="p-3 pb-0 flex-shrink-0">
							<TabsList className="grid w-full grid-cols-2 h-10 rounded-xl p-1">
								<TabsTrigger value="info" className={tabTriggerClass}>
									Info
								</TabsTrigger>
								<TabsTrigger value="metadata" className={tabTriggerClass}>
									Metadata
								</TabsTrigger>
							</TabsList>
						</div>

						<TabsContent value="info" className="flex-1 m-0 overflow-hidden">
							<ScrollArea className="h-full">
								<div className="p-4">
									<FileInfo
										file={file}
										downloadCount={shareData.shareDownloadCount}
										dateStaged={asset.dateStaged}
										dateLive={asset.dateLive}
									/>
								</div>
							</ScrollArea>
						</TabsContent>

						<TabsContent value="metadata" className="flex-1 m-0 overflow-hidden">
							<ScrollArea className="h-full">
								<div className="p-4">
									<MetadataPanel metadata={asset.metadata} />
								</div>
							</ScrollArea>
						</TabsContent>
					</Tabs>
				</div>
			</div>
		</div>
	)
}
